<template>
  <ChoosePaymentMethodWayDialog
    :is-show-dialog="status.isShowChoosePaymentMethodWayDialog"
    :caller="caller"
    :plan="plan"
    @choose-payment-method="onFallbackChosen"
    @card-added="onCardAddedViaLink"
    @cancel="status.isShowChoosePaymentMethodWayDialog = false"
  />

  <AddPaymentMethodDialog
    :is-show-dialog="status.isShowManualPaymentDialog"
    :show-phone-number="showPhoneNumber"
    :home-address="homeAddress"
    :use3ds="use3ds"
    :is-registration="isRegistration"
    :caller-id="caller?.id"
    :plan="plan"
    :subscription-start-at="subscriptionStartAt"
    @submit="setPaymentInfo"
    @cancel="hidePaymentMethodDialog"
  />

  <SendEmailPaymentLinkDialog
    :is-show-dialog="status.isShowEmailLinkDialog"
    :customer-email="caller?.email ?? ''"
    @submit="sendPaymentLinkEmail"
    @cancel="hideEmailLinkDialog"
  />

  <ConfirmPaymentLinkSentDialog
    :is-show-dialog="status.isShowConfirmPaymentSentLinkDialog"
    :callerId="caller?.id ?? ''"
    :link-sent-destination="linkSentDestination"
    @cancel="hidePaymentLinkConfirmDialog"
    @card-added="onCardAddedViaLink"
  />
</template>

<script setup lang="ts">
import { ElLoading } from 'element-plus';
import { defineAsyncComponent, reactive, ref } from 'vue';

import util from '@/Utils/util';
import { CustomLocationType } from '@/models/address';
import { Caller } from '@/models/caller';
import { BillingAddress } from '@/models/payment';
import { callerService } from '@/services/caller';
import { useSwalDialog } from '@/use/swalDialog';
import type { StripeIntentKind } from '@/use/stripe';

const ChoosePaymentMethodWayDialog = defineAsyncComponent(() => import('@/components/Main/Dialogs/ChoosePaymentMethodWay.vue') as any);
const AddPaymentMethodDialog = defineAsyncComponent(() => import('@/components/Main/Dialogs/AddPaymentMethod.vue') as any);
const SendEmailPaymentLinkDialog = defineAsyncComponent(() => import('@/components/Main/Dialogs/Payment/SendEmailPaymentLink.vue') as any);
const ConfirmPaymentLinkSentDialog = defineAsyncComponent(() => import('@/components/Main/Dialogs/Payment/ConfirmPaymentLinkSent.vue') as any);

type FallbackChoice = 'manual' | 'email';

const props = defineProps<{
  homeAddress: CustomLocationType;
  use3ds?: boolean;
  showPhoneNumber?: boolean;
  isRegistration?: boolean;
  caller?: Caller | null;
  plan?: string;
  subscriptionStartAt?: string;
}>();

const emit = defineEmits(['submit', 'hide', 'set-payment-info', 'card-added']);

const { showErrorPopupDialog } = useSwalDialog();

const status = reactive({
  isShowChoosePaymentMethodWayDialog: false,
  isShowManualPaymentDialog: false,
  isShowEmailLinkDialog: false,
  isShowConfirmPaymentSentLinkDialog: false,
});

const linkSentDestination = ref('');

const onFallbackChosen = (method: FallbackChoice) => {
  status.isShowChoosePaymentMethodWayDialog = false;
  if (method === 'manual') {
    status.isShowManualPaymentDialog = true;
  } else {
    status.isShowEmailLinkDialog = true;
  }
};

const setPaymentInfo = (data: {
  setupIntentId: string;
  confirmationTokenId?: string;
  intentKind?: StripeIntentKind;
  billingAddress: BillingAddress;
  cardHolderName: string;
  cardHolderPhoneNumber: string;
  cardHolderType: string;
  use3ds: boolean;
}) => {
  status.isShowManualPaymentDialog = false;
  emit('set-payment-info', data);
};

const hidePaymentMethodDialog = () => {
  status.isShowManualPaymentDialog = false;
};

const hideEmailLinkDialog = () => {
  status.isShowEmailLinkDialog = false;
};

const hidePaymentLinkConfirmDialog = () => {
  status.isShowConfirmPaymentSentLinkDialog = false;
};

const onCardAddedViaLink = () => {
  status.isShowConfirmPaymentSentLinkDialog = false;
  status.isShowChoosePaymentMethodWayDialog = false;

  if (props.isRegistration) {
    emit('card-added');
  }
};

const setDialogStatus = ({
  isShowChoosePaymentMethodWayDialog,
  isShowManualPaymentDialog,
}: {
  isShowChoosePaymentMethodWayDialog?: boolean;
  isShowManualPaymentDialog?: boolean;
}) => {
  status.isShowChoosePaymentMethodWayDialog = isShowChoosePaymentMethodWayDialog ?? false;
  status.isShowManualPaymentDialog = isShowManualPaymentDialog ?? false;
};

const sendPaymentLinkEmail = async (emailAddress: string) => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Sending Payment Link Email...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await callerService.sendCreditCardFormLinkEmail({
      callerId: props.caller?.id ?? '',
      emailAddress,
      plan: props.plan,
    });

    linkSentDestination.value = emailAddress;
    status.isShowEmailLinkDialog = false;
    status.isShowConfirmPaymentSentLinkDialog = true;
  } catch (err) {
    const errorText = util.getGraphqlError(err, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    loading.close();
  }
};

defineExpose({
  setDialogStatus,
});
</script>
